import api, { handleApiError } from './api';
import { LoginCredentials, RegisterData, LoginResponse, RegisterResponse, TokenPair } from '../types/auth';

export interface PreRegisterOTPData {
  email: string;
  username?: string;
  date_of_birth?: string;
  age_confirmation?: boolean;
  policy_acceptance?: boolean;
  enforce_signup_compliance?: boolean;
  recaptcha_token?: string;
}

export interface UsernameAvailability {
  username: string;
  available: boolean;
  valid: boolean;
  message: string;
}

export const authService = {
  /**
   * Login user with email/username + password
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>('/auth/login', credentials);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /** Check a username while the registration form is being completed. */
  async checkUsernameAvailability(
    username: string,
    signal?: AbortSignal
  ): Promise<UsernameAvailability> {
    try {
      const response = await api.get<UsernameAvailability>(
        '/auth/register/username-availability',
        {
          params: { username },
          signal,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /** Step 1 of multi-step registration: send OTP to email */
  async sendPreRegisterOTP(data: string | PreRegisterOTPData): Promise<{ success: boolean; message: string }> {
    try {
      const payload = typeof data === 'string' ? { email: data } : data;
      const response = await api.post('/auth/register/send-otp', payload);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /** Step 2: verify OTP sent before registration */
  async verifyPreRegisterOTP(email: string, otp: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/auth/register/verify-otp', { email, otp });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /** Send OTP for email login (passwordless) */
  async sendLoginEmailOTP(email: string, recaptchaToken?: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/auth/login/email-otp/send', { email, recaptcha_token: recaptchaToken });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /** Verify email OTP and login */
  async verifyLoginEmailOTP(email: string, otp: string): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>('/auth/login/email-otp/verify', { email, otp });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /** Send OTP for mobile login (mobile must be verified) */
  async sendLoginMobileOTP(phone: string, recaptchaToken?: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/auth/login/mobile-otp/send', { phone, recaptcha_token: recaptchaToken });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /** Verify mobile OTP and login */
  async verifyLoginMobileOTP(phone: string, otp: string): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>('/auth/login/mobile-otp/verify', { phone, otp });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Register new user
   */
  async register(data: RegisterData): Promise<RegisterResponse> {
    try {
      const response = await api.post<RegisterResponse>('/auth/register', data);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Logout locally even if API call fails
      console.error('Logout error:', error);
    }
  },

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(refreshToken: string): Promise<TokenPair> {
    try {
      const response = await api.post<TokenPair>('/auth/refresh', {
        refresh_token: refreshToken,
      });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Setup 2FA for user — returns QR code, secret, and backup codes
   */
  async setup2FA(): Promise<{ secret: string; qr_code_url: string; backup_codes: string[] }> {
    try {
      const response = await api.post('/auth/2fa/setup');
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Verify TOTP code to enable 2FA (requires auth)
   */
  async verify2FA(code: string): Promise<{ success: boolean }> {
    try {
      const response = await api.post('/auth/2fa/verify', { totp_code: code });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Disable 2FA with current TOTP code (requires auth)
   */
  async disable2FA(code: string): Promise<{ success: boolean }> {
    try {
      const response = await api.post('/auth/2fa/disable', { totp_code: code });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Complete a pending 2FA login — no auth required.
   * Called after /auth/login returns two_fa_required=true.
   */
  async verify2FALogin(userId: number, totpCode: string): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>('/auth/2fa/verify-login', {
        user_id: userId,
        totp_code: totpCode,
      });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },
};
